/**
 * Filter bar for the module list (AR-328).
 *
 * The toggle button, the type legend and the expandable checkbox grid sit above
 * the list so the filters read as part of the table, not a separate dialog.
 */
import React from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ACTIVATION_FILTER_GROUPS,
  LEGEND_TYPES,
  STEP_TYPE_STYLES,
  type FilterGroupId,
} from '@/lib/activationBuilder';

/** Selected option ids, keyed by filter-group id. Never display labels. */
export type ActivationFilterState = Partial<Record<FilterGroupId, string[]>>;

interface ActivationFiltersProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selected: ActivationFilterState;
  onToggleOption: (groupId: FilterGroupId, optionId: string) => void;
  onClear: () => void;
  /** Right-hand actions (Add module / Add step). */
  actions?: React.ReactNode;
}

export const ActivationFilters: React.FC<ActivationFiltersProps> = ({
  open,
  onOpenChange,
  selected,
  onToggleOption,
  onClear,
  actions,
}) => {
  const activeCount = Object.values(selected).reduce((sum, list) => sum + (list?.length ?? 0), 0);

  return (
    <div className="mb-5">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          variant={open ? 'default' : 'outline'}
          onClick={() => onOpenChange(!open)}
          aria-expanded={open}
          className={cn(open && 'bg-gray-900 text-white hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-900')}
        >
          <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
          Filters
          {activeCount > 0 && (
            <span className="ml-1.5 rounded-full bg-orange-500 px-1.5 text-[10px] font-semibold text-white">
              {activeCount}
            </span>
          )}
        </Button>

        <div className="flex flex-wrap items-center gap-2">
          {LEGEND_TYPES.map((type) => (
            <span
              key={type}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[10.5px] text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400"
            >
              <span className={cn('h-1.5 w-1.5 rounded-full', STEP_TYPE_STYLES[type].dot)} aria-hidden />
              {type}
            </span>
          ))}
        </div>

        {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
      </div>

      {open && (
        <div className="mt-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-6">
            {ACTIVATION_FILTER_GROUPS.map((group) => (
              <fieldset key={group.id}>
                <legend className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500">
                  {group.name}
                </legend>
                <div className="space-y-1.5">
                  {group.options.map((option) => {
                    const id = `filter-${group.id}-${option.id}`.replace(/[^a-zA-Z0-9-]+/g, '-');
                    return (
                      <label
                        key={option.id}
                        htmlFor={id}
                        className="flex cursor-pointer items-center gap-2 text-xs text-gray-600 dark:text-gray-300"
                      >
                        <Checkbox
                          id={id}
                          checked={(selected[group.id] ?? []).includes(option.id)}
                          onCheckedChange={() => onToggleOption(group.id, option.id)}
                        />
                        {option.label}
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            ))}
          </div>
          {activeCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              className="mt-3 h-7 px-2 text-xs text-gray-500"
            >
              Clear all filters
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivationFilters;
